/**
 * AWS Icon Registry
 *
 * Maps AWS service identifiers to:
 *   - semanticType: provider-agnostic ontology type (postgres, mongodb, server, ...)
 *   - serviceIcon / resourceIcon / groupIcon: CDN-loadable AWS Architecture Icons
 *   - color: hex color for accent rendering
 *
 * The semantic type is what the canvas, Compass rules, and AI tools use.
 * The icon is what the human sees. They're decoupled — Azure and GCP shape
 * registries can map their services to the SAME semantic type and the
 * cross-provider question "show me all my postgres databases" just works.
 */

import { isA } from '../../../lib/graph/ontology';
import { listProviderIconCatalogEntries } from '../providers/providerIconResolver';

/**
 * AWS architecture icons — sourced from the `aws-icons@3.3.0` npm package.
 *
 * Icons are loaded from a CDN at runtime rather than bundled, to keep the
 * editor entry chunk small. The default points at `cdn.jsdelivr.net` (a
 * third-party CDN) — for production deployments that disallow third-party
 * asset CDNs, set `VITE_AWS_ICON_BASE` at build time to a self-hosted or
 * org-CDN mirror of the same `aws-icons@3.3.0/icons` directory tree.
 *
 * TODO(epic-h): bundle the icons through Vite (`import.meta.glob` over
 * `node_modules/aws-icons/icons/*.svg` + a copy plugin) and remove the
 * runtime CDN dependency entirely. Tracked because the `aws-icons` package
 * is already a dev dependency, so the bytes are local — only the build
 * pipeline is missing.
 */
const DEFAULT_CDN_BASE = 'https://cdn.jsdelivr.net/npm/aws-icons@3.3.0/icons';
const CDN_BASE =
  (typeof import.meta !== 'undefined' && (import.meta as { env?: Record<string, string> }).env?.VITE_AWS_ICON_BASE) ||
  DEFAULT_CDN_BASE;

export interface AwsIconEntry {
  /** Provider-agnostic ontology type. Compass and AI tools query this. */
  semanticType: string;
  /** Default architecture-service icon path (relative to CDN_BASE). */
  serviceIcon: string;
  /** Optional resource-level icon for finer detail. */
  resourceIcon?: string;
  /** Optional architecture-group icon for container shapes. */
  groupIcon?: string;
  /** AWS service category (Networking, Compute, ...) for grouping in palettes. */
  category: string;
  /** Hex color used as the accent stripe on shape rendering. */
  color: string;
}

/**
 * AWS service ID → semantic type + icon
 *
 * Service IDs are short slugs that match AWS CLI / SDK names where possible.
 * Multiple AWS products can map to the same semantic type when appropriate
 * (e.g. RDS Postgres and Aurora Postgres both map to `postgres`).
 */
export const AWS_ICON_REGISTRY: Record<string, AwsIconEntry> = {
  // ─── Networking (place) ──────────────────────────────
  vpc:             { semanticType: 'network_boundary', serviceIcon: 'architecture-service/AmazonVPCLattice', groupIcon: 'architecture-group/VirtualprivatecloudVPC', category: 'Networking', color: '#8b5cf6' },
  subnet:          { semanticType: 'subnet',           serviceIcon: 'architecture-service/AmazonVPCLattice', groupIcon: 'architecture-group/Publicsubnet',           category: 'Networking', color: '#a78bfa' },
  subnet_public:   { semanticType: 'subnet',           serviceIcon: 'architecture-service/AmazonVPCLattice', groupIcon: 'architecture-group/Publicsubnet',           category: 'Networking', color: '#22c55e' },
  subnet_private:  { semanticType: 'subnet',           serviceIcon: 'architecture-service/AmazonVPCLattice', groupIcon: 'architecture-group/Privatesubnet',          category: 'Networking', color: '#6366f1' },
  region:          { semanticType: 'geographic_region', serviceIcon: 'architecture-group/AWSCloud',                                                                  category: 'Networking', color: '#64748b' },
  az:              { semanticType: 'availability_zone', serviceIcon: 'architecture-group/AWSCloud',                                                                  category: 'Networking', color: '#94a3b8' },
  account:         { semanticType: 'account',          serviceIcon: 'architecture-group/AWSAccount',                                                                 category: 'Networking', color: '#475569' },
  transit_gateway: { semanticType: 'network_edge',     serviceIcon: 'architecture-service/AWSTransitGateway',                                                        category: 'Networking', color: '#8b5cf6' },

  // ─── Compute (artifact → compute) ────────────────────
  ec2:             { semanticType: 'server',            serviceIcon: 'architecture-service/AmazonEC2',           resourceIcon: 'resource/AmazonEC2Instance', category: 'Compute',    color: '#f97316' },
  lambda:          { semanticType: 'function_service',  serviceIcon: 'architecture-service/AWSLambda',                                                       category: 'Compute',    color: '#f59e0b' },
  ecs:             { semanticType: 'container_orchestrator', serviceIcon: 'architecture-service/AmazonECSAnywhere',                                          category: 'Containers', color: '#f97316' },
  eks:             { semanticType: 'kubernetes_cluster',     serviceIcon: 'architecture-service/AmazonEKSAnywhere',                                          category: 'Containers', color: '#f97316' },
  fargate:         { semanticType: 'container_instance',     serviceIcon: 'architecture-service/AWSFargate',                                                 category: 'Containers', color: '#f97316' },
  app_runner:      { semanticType: 'managed_compute',        serviceIcon: 'architecture-service/AWSAppRunner',                                               category: 'Compute',    color: '#f97316' },
  batch:           { semanticType: 'managed_compute',        serviceIcon: 'architecture-service/AWSBatch',                                                   category: 'Compute',    color: '#f97316' },
  step_functions:  { semanticType: 'function_service',       serviceIcon: 'architecture-service/AWSStepFunctions',                                           category: 'Compute',    color: '#ec4899' },
  lightsail:       { semanticType: 'server',                 serviceIcon: 'architecture-service/AmazonLightsail',                                            category: 'Compute',    color: '#f97316' },

  // ─── Load balancing & API edge (artifact → network_path) ─────
  alb:             { semanticType: 'load_balancer', serviceIcon: 'architecture-service/ElasticLoadBalancing',                              category: 'Networking', color: '#8b5cf6' },
  nlb:             { semanticType: 'load_balancer', serviceIcon: 'architecture-service/ElasticLoadBalancing',                              category: 'Networking', color: '#8b5cf6' },
  clb:             { semanticType: 'load_balancer', serviceIcon: 'architecture-service/ElasticLoadBalancing',                              category: 'Networking', color: '#8b5cf6' },
  api_gateway:     { semanticType: 'api_gateway',   serviceIcon: 'architecture-service/AmazonAPIGateway',                                  category: 'Networking', color: '#ec4899' },

  // ─── Network edge (artifact → network_path → network_edge) ──
  igw:             { semanticType: 'network_edge', serviceIcon: 'architecture-service/AmazonVPCLattice', resourceIcon: 'resource/AmazonVPCInternetGateway', category: 'Networking', color: '#6366f1' },
  nat:             { semanticType: 'network_edge', serviceIcon: 'architecture-service/AmazonVPCLattice', resourceIcon: 'resource/AmazonVPCNATGateway',      category: 'Networking', color: '#6366f1' },
  vpc_endpoint:    { semanticType: 'network_edge', serviceIcon: 'architecture-service/AmazonVPCLattice', resourceIcon: 'resource/AmazonVPCEndpoints',       category: 'Networking', color: '#6366f1' },
  vpc_peering:     { semanticType: 'network_edge', serviceIcon: 'architecture-service/AmazonVPCLattice',                                                    category: 'Networking', color: '#6366f1' },
  cloudfront:      { semanticType: 'cdn',          serviceIcon: 'architecture-service/AmazonCloudFront',                                                    category: 'Networking', color: '#6366f1' },
  route53:         { semanticType: 'dns_zone',     serviceIcon: 'architecture-service/AmazonRoute53',                                                       category: 'Networking', color: '#6366f1' },
  route53_record:  { semanticType: 'dns_record',   serviceIcon: 'architecture-service/AmazonRoute53',                                                       category: 'Networking', color: '#6366f1' },
  route_table:     { semanticType: 'route_table',  serviceIcon: 'architecture-service/AmazonVPCLattice',                                                    category: 'Networking', color: '#6366f1' },
  alb_listener:    { semanticType: 'network_path', serviceIcon: 'architecture-service/ElasticLoadBalancing',                                                category: 'Networking', color: '#8b5cf6' },
  target_group:    { semanticType: 'load_balancer_target', serviceIcon: 'architecture-service/ElasticLoadBalancing',                                        category: 'Networking', color: '#8b5cf6' },

  // ─── Storage (datastore subtypes) ────────────────────
  s3:              { semanticType: 's3_bucket',  serviceIcon: 'architecture-service/AmazonSimpleStorageService',         category: 'Storage', color: '#22c55e' },
  ebs:             { semanticType: 'ebs_volume', serviceIcon: 'architecture-service/AmazonEBS',                          category: 'Storage', color: '#22c55e' },
  efs:             { semanticType: 'efs_volume', serviceIcon: 'architecture-service/AmazonEFS',                          category: 'Storage', color: '#22c55e' },
  glacier:         { semanticType: 's3_bucket',  serviceIcon: 'architecture-service/AmazonSimpleStorageServiceGlacier', category: 'Storage', color: '#22c55e' },

  // ─── Databases (datastore → database → ...) ──────────
  rds_postgres:    { semanticType: 'postgres',           serviceIcon: 'architecture-service/AmazonRDS',         category: 'Database', color: '#3b82f6' },
  rds_mysql:       { semanticType: 'mysql',              serviceIcon: 'architecture-service/AmazonRDS',         category: 'Database', color: '#3b82f6' },
  rds_mariadb:     { semanticType: 'mariadb',            serviceIcon: 'architecture-service/AmazonRDS',         category: 'Database', color: '#3b82f6' },
  rds_oracle:      { semanticType: 'oracle',             serviceIcon: 'architecture-service/AmazonRDS',         category: 'Database', color: '#3b82f6' },
  rds_sqlserver:   { semanticType: 'sqlserver',          serviceIcon: 'architecture-service/AmazonRDS',         category: 'Database', color: '#3b82f6' },
  // Generic RDS (engine unknown at scan time) — falls back to abstract
  rds:             { semanticType: 'relational_database', serviceIcon: 'architecture-service/AmazonRDS',         category: 'Database', color: '#3b82f6' },
  aurora_postgres: { semanticType: 'aurora_postgres',    serviceIcon: 'architecture-service/AmazonAurora',      category: 'Database', color: '#3b82f6' },
  aurora_mysql:    { semanticType: 'aurora_mysql',       serviceIcon: 'architecture-service/AmazonAurora',      category: 'Database', color: '#3b82f6' },
  dynamodb:        { semanticType: 'dynamodb',           serviceIcon: 'architecture-service/AmazonDynamoDB',    category: 'Database', color: '#3b82f6' },
  documentdb:      { semanticType: 'mongodb',            serviceIcon: 'architecture-service/AmazonDocumentDB',  category: 'Database', color: '#3b82f6' },
  elasticache_redis:     { semanticType: 'redis',     serviceIcon: 'architecture-service/AmazonElastiCache',     category: 'Database', color: '#3b82f6' },
  elasticache_memcached: { semanticType: 'memcached', serviceIcon: 'architecture-service/AmazonElastiCache',     category: 'Database', color: '#3b82f6' },
  elasticache:           { semanticType: 'key_value_store', serviceIcon: 'architecture-service/AmazonElastiCache', category: 'Database', color: '#3b82f6' },
  redshift:        { semanticType: 'redshift',           serviceIcon: 'architecture-service/AmazonRedshift',    category: 'Database', color: '#3b82f6' },
  neptune:         { semanticType: 'neptune',            serviceIcon: 'architecture-service/AmazonNeptune',     category: 'Database', color: '#3b82f6' },
  timestream:      { semanticType: 'timestream',         serviceIcon: 'architecture-service/AmazonTimestream',  category: 'Database', color: '#3b82f6' },
  opensearch:      { semanticType: 'opensearch',         serviceIcon: 'architecture-service/AmazonOpenSearchService', category: 'Database', color: '#3b82f6' },

  // ─── Identity (agent subtypes) ───────────────────────
  iam_role:        { semanticType: 'service_identity',   serviceIcon: 'architecture-service/AWSIdentityandAccessManagement', resourceIcon: 'resource/AWSIdentityAccessManagementRole', category: 'Security', color: '#f59e0b' },
  iam_user:        { semanticType: 'user_identity',      serviceIcon: 'architecture-service/AWSIdentityandAccessManagement', category: 'Security', color: '#f59e0b' },
  iam_group:       { semanticType: 'identity_group',     serviceIcon: 'architecture-service/AWSIdentityandAccessManagement', category: 'Security', color: '#f59e0b' },
  iam_policy:      { semanticType: 'access_policy',      serviceIcon: 'architecture-service/AWSIdentityandAccessManagement', category: 'Security', color: '#f59e0b' },
  cognito:         { semanticType: 'cognito_user_pool',  serviceIcon: 'architecture-service/AmazonCognito',                  category: 'Security', color: '#ef4444' },
  sso:             { semanticType: 'identity_provider',  serviceIcon: 'architecture-service/AWSIAMIdentityCenter',           category: 'Security', color: '#ef4444' },

  // ─── Security (policy subtypes) ──────────────────────
  sg:              { semanticType: 'network_acl',       serviceIcon: 'architecture-service/AWSNetworkFirewall',     category: 'Security', color: '#ef4444' },
  nacl:            { semanticType: 'network_acl',       serviceIcon: 'architecture-service/AWSNetworkFirewall',     category: 'Security', color: '#ef4444' },
  waf:             { semanticType: 'web_app_firewall',  serviceIcon: 'architecture-service/AWSWAF',                 category: 'Security', color: '#ef4444' },
  shield:          { semanticType: 'web_app_firewall',  serviceIcon: 'architecture-service/AWSShield',              category: 'Security', color: '#ef4444' },
  kms:             { semanticType: 'encryption_key',    serviceIcon: 'architecture-service/AWSKeyManagementService', category: 'Security', color: '#a855f7' },
  acm:              { semanticType: 'certificate',       serviceIcon: 'architecture-service/AWSCertificateManager',  category: 'Security', color: '#ef4444' },
  secrets_manager: { semanticType: 'secret_store',      serviceIcon: 'architecture-service/AWSSecretsManager',      category: 'Security', color: '#ef4444' },
  guardduty:       { semanticType: 'threat_detector',   serviceIcon: 'architecture-service/AmazonGuardDuty',        category: 'Security', color: '#ef4444' },
  inspector:       { semanticType: 'threat_detector',   serviceIcon: 'architecture-service/AmazonInspector',        category: 'Security', color: '#ef4444' },

  // ─── Messaging (artifact → messaging_artifact) ───────
  sns:             { semanticType: 'message_topic', serviceIcon: 'architecture-service/AmazonSimpleNotificationService', category: 'App Integration', color: '#ec4899' },
  sqs:             { semanticType: 'message_queue', serviceIcon: 'architecture-service/AmazonSimpleQueueService',         category: 'App Integration', color: '#ec4899' },
  eventbridge:     { semanticType: 'event_bus',     serviceIcon: 'architecture-service/AmazonEventBridge',                category: 'App Integration', color: '#ec4899' },
  kinesis:         { semanticType: 'event_stream',  serviceIcon: 'architecture-service/AmazonKinesis',                    category: 'Analytics',       color: '#8b5cf6' },

  // ─── Observability (artifact → observability_artifact) ──
  cloudwatch:      { semanticType: 'metric_store', serviceIcon: 'architecture-service/AmazonCloudWatch', category: 'Management', color: '#ec4899' },
  cloudwatch_logs: { semanticType: 'log_store',    serviceIcon: 'architecture-service/AmazonCloudWatch', category: 'Management', color: '#ec4899' },
  cloudwatch_alarm:{ semanticType: 'monitoring_alarm', serviceIcon: 'architecture-service/AmazonCloudWatch', category: 'Management', color: '#ec4899' },
  cloudtrail:      { semanticType: 'audit_log',    serviceIcon: 'architecture-service/AWSCloudTrail',    category: 'Management', color: '#ec4899' },
  config:          { semanticType: 'audit_log',    serviceIcon: 'architecture-service/AWSConfig',        category: 'Management', color: '#ec4899' },
  xray:            { semanticType: 'trace_store',  serviceIcon: 'architecture-service/AWSXRay',          category: 'Management', color: '#ec4899' },
  systems_manager: { semanticType: 'managed_compute', serviceIcon: 'architecture-service/AWSSystemsManager', category: 'Management', color: '#ec4899' },
  autoscaling_group: { semanticType: 'autoscaling_group', serviceIcon: 'architecture-service/AWSAutoScaling',  category: 'Management', color: '#f97316' },

  // ─── AI/ML ───────────────────────────────────────────
  sagemaker:       { semanticType: 'managed_compute', serviceIcon: 'architecture-service/AmazonSageMaker', category: 'AI/ML', color: '#22c55e' },
  bedrock:         { semanticType: 'managed_compute', serviceIcon: 'architecture-service/AmazonBedrock',   category: 'AI/ML', color: '#22c55e' },
};

// ─── Lookup helpers ────────────────────────────────────

export function getIconUrl(serviceId: string, variant: 'service' | 'resource' | 'group' = 'service'): string | null {
  const entry = AWS_ICON_REGISTRY[serviceId];
  if (!entry) return null;
  const iconPath = variant === 'resource' && entry.resourceIcon
    ? entry.resourceIcon
    : variant === 'group' && entry.groupIcon
      ? entry.groupIcon
      : entry.serviceIcon;
  const localIcon = findLocalAwsIcon(serviceId, iconPath);
  if (localIcon) return localIcon;
  return `${CDN_BASE}/${iconPath}.svg`;
}

export function getSemanticType(serviceId: string): string {
  return AWS_ICON_REGISTRY[serviceId]?.semanticType ?? 'artifact';
}

export function getServiceColor(serviceId: string): string {
  return AWS_ICON_REGISTRY[serviceId]?.color ?? '#64748b';
}

export function getServiceCategory(serviceId: string): string {
  return AWS_ICON_REGISTRY[serviceId]?.category ?? 'Other';
}

export function getAllRegisteredServices(): string[] {
  return Object.keys(AWS_ICON_REGISTRY);
}

/**
 * Find AWS services whose semantic type matches an abstract ontology type.
 *
 * @example
 *   getServicesBySemanticAncestor('database')
 *   // ['rds_postgres', 'rds_mysql', 'aurora_postgres', 'dynamodb', 'documentdb', ...]
 */
export function getServicesBySemanticAncestor(ancestorType: string): string[] {
  return Object.entries(AWS_ICON_REGISTRY)
    .filter(([, entry]) => isA(entry.semanticType, ancestorType))
    .map(([serviceId]) => serviceId);
}

export function getServicesByCategory(category: string): string[] {
  return Object.entries(AWS_ICON_REGISTRY)
    .filter(([, entry]) => entry.category === category)
    .map(([serviceId]) => serviceId);
}

function findLocalAwsIcon(serviceId: string, iconPath: string): string | null {
  const icons = listProviderIconCatalogEntries('aws');
  if (icons.length === 0) return null;
  const hints = [
    serviceId,
    iconPath.split('/').pop() ?? iconPath,
    camelToWords(iconPath.split('/').pop() ?? iconPath),
  ].map(normalizeToken).filter(Boolean);

  for (const hint of hints) {
    const exact = icons.find((icon) => icon.tokens.some((token) => normalizeToken(token) === hint));
    if (exact) return exact.url;
  }
  for (const hint of hints) {
    const fuzzy = icons.find((icon) =>
      icon.tokens.some((token) => {
        const normalized = normalizeToken(token);
        return normalized.includes(hint) || hint.includes(normalized);
      }),
    );
    if (fuzzy) return fuzzy.url;
  }
  return null;
}

function camelToWords(value: string): string {
  return value
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2');
}

function normalizeToken(value: string): string {
  return value
    .toLowerCase()
    .replace(/aws/g, '')
    .replace(/amazon/g, '')
    .replace(/[^a-z0-9]+/g, '')
    .trim();
}
